import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, MoreVertical, CheckCircle2, Smartphone } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const ANDROID_STEP_IMGS = ["/install-and-1.png", "/install-and-2.png", "/install-and-3.png"];

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export default function InstallApp({ open, onClose }) {
  const { t } = useI18n();
  const [canPrompt, setCanPrompt] = useState(!!window.__deferredInstallPrompt);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onInstallable = () => setCanPrompt(true);
    const onInstalled = () => { setInstalled(true); setCanPrompt(false); };
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const doInstall = async () => {
    const p = window.__deferredInstallPrompt;
    if (!p) return;
    p.prompt();
    await p.userChoice;
    window.__deferredInstallPrompt = null;
    setCanPrompt(false);
  };

  const steps = [
    { icon: MoreVertical, text: t("ia_and_1"), img: ANDROID_STEP_IMGS[0] },
    { icon: Download, text: t("ia_and_2"), img: ANDROID_STEP_IMGS[1] },
    { icon: CheckCircle2, text: t("ia_and_3"), img: ANDROID_STEP_IMGS[2] },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-md max-h-[85vh] overflow-y-auto" data-testid="install-app-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold flex items-center gap-2">
            <Smartphone className="text-[#007BFF]" size={20} /> {t("ia_title")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-white/60 text-sm leading-relaxed -mt-1">{t("ia_desc")}</p>

        {installed ? (
          <div className="mt-3 flex items-center gap-2 rounded-[12px] border border-[#00E676]/40 bg-[#00E676]/10 p-3 text-sm text-[#00E676]" data-testid="install-already">
            <CheckCircle2 size={18} /> {t("ia_installed")}
          </div>
        ) : (
          <div className="mt-3 space-y-4">
            {canPrompt && (
              <Button data-testid="install-now-btn" onClick={doInstall}
                className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-12 font-semibold text-base">
                <Download size={18} className="mr-2" /> {t("ia_install_btn")}
              </Button>
            )}

            <div data-testid="install-android-steps">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-3">{t("ia_and_title")}</div>
              <div className="space-y-4">
                {steps.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="flex gap-3">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-[#007BFF]/20 border border-[#007BFF]/40 flex items-center justify-center text-xs font-bold text-[#007BFF]">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm text-white/90">
                          <Icon size={16} className="text-[#00E676] shrink-0" /> {s.text}
                        </div>
                        <img src={s.img} alt={`etape-${i + 1}`} loading="lazy"
                          className="mt-2 rounded-[10px] border border-white/10 w-full max-w-[240px] mx-auto block" data-testid={`install-img-${i}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
